/** The install prompt (S04) and the lead alerts card (docs/54 M8: the app says when alerts are off and how to fix it). */
import { Banner, Button, Card, CardHead, Icon, Sheet } from '@ff/app-ui';
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useStore } from '../lib/store.ts';
import { toast } from '../lib/ui.ts';
import { S } from '@ff/app-ui/copy';
import { installEvent, isStandalone, noteInstallDismissed, noteInstallShown, platform, pushState, shouldOfferInstall, turnOffPush, turnOnPush, type PushState } from './pwa.ts';

export function InstallCard({ force = false }: { force?: boolean }): JSX.Element | null {
  const [show, setShow] = useState(() => force || shouldOfferInstall());
  const [open, setOpen] = useState(false);
  const evt = useStore(installEvent);
  useEffect(() => {
    if (show && !force) noteInstallShown();
  }, []);
  if (!show || isStandalone()) return null;
  const plat = platform();
  const isSafari = /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);
  return (
    <Card class="install-card">
      <CardHead title={S.install.title} />
      <p>{S.install.lead}</p>
      <div class="card-foot btn-row">
        <Button
          variant="primary"
          icon="download"
          onClick={async () => {
            if (evt) {
              await evt.prompt();
              const c = await evt.userChoice;
              installEvent.set(null);
              if (c.outcome === 'accepted') setShow(false);
            } else setOpen(true);
          }}
        >
          {evt ? S.install.installNow : S.install.show}
        </Button>
        <Button
          variant="quiet"
          onClick={() => {
            noteInstallDismissed();
            setShow(false);
          }}
        >
          {S.install.later}
        </Button>
      </div>
      <Sheet open={open} onClose={() => setOpen(false)} title={S.install.title} closeLabel={S.app.close}>
        <div class="stack">
          {plat === 'ios' && !isSafari && <Banner tone="warn">{S.install.iosNotSafari}</Banner>}
          <ol class="steps" role="list">
            {(plat === 'ios' ? S.install.iosSteps : S.install.androidSteps).map((step, i) => (
              <li key={i}>
                <span class="step-n" aria-hidden="true">{i + 1}</span>
                <div class="stack" data-gap="s">
                  <p>{step}</p>
                  {plat === 'ios' && i === 0 && (
                    <p class="install-pic">
                      <span class="share-glyph" aria-hidden="true">
                        <Icon name="share" size={28} />
                      </span>
                      <span class="t-small t-muted">{S.install.shareIcon}</span>
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </Sheet>
    </Card>
  );
}

export function PushCard({ compact = false }: { compact?: boolean }): JSX.Element | null {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void pushState().then(setState);
  }, []);
  if (!state) return null;
  // On Home only nudge when it's actionable; the settings screen always shows the full state.
  if (compact && (state === 'on' || state === 'unsupported' || state === 'needs_install')) return null;
  const line = { on: S.push.on, off: S.push.off, denied: S.push.denied, needs_install: S.push.needsInstall, unsupported: S.push.unsupported }[state];
  return (
    <Card class="push-card">
      <CardHead title={S.push.cardTitle} />
      <p>{line}</p>
      {(state === 'off' || state === 'on') && (
        <div class="card-foot">
          <Button
            variant={state === 'off' ? 'primary' : 'secondary'}
            icon={state === 'off' ? 'bell' : undefined}
            busy={busy}
            onClick={async () => {
              setBusy(true);
              try {
                setState(state === 'off' ? await turnOnPush() : await turnOffPush());
              } catch {
                toast(S.push.failed);
              } finally {
                setBusy(false);
              }
            }}
          >
            {state === 'off' ? S.push.turnOn : S.push.turnOff}
          </Button>
        </div>
      )}
    </Card>
  );
}
